var FlickityCarouselMobileCollectionCards = class extends HTMLElement {
	constructor() {
		super();

		this._isReady = false;
		this._isInitializing = false;
		this._isDisabledByLoading = false;
		this._unsubLoading = null;
		this._allowAfter = performance.now() + 3000;
		this._isDragging = false;
		this._root = this.querySelector(".product-item__mobile-carousel");
		this._cells = this._root
			? Array.from(
					this._root.querySelectorAll(
						".product-item__mobile-carousel-cell"
					)
				)
			: [];

		if (this._root) {
			this._root.setAttribute("data-carousel-owned", "true");
			this._cells.forEach((cell) => {
				const img = cell.querySelector("img");
				if (img) {
					const wrap =
						img.closest(".product-item__image-wrapper") ||
						this._root;
					wrap.setAttribute("data-carousel-owned", "true");
				}
				cell.setAttribute("data-carousel-owned", "true");
			});
		}

		this.setAttribute("aria-busy", "true");
		this.style.pointerEvents = "none";

		this._onPointerDown = this._onPointerDown.bind(this);
		this.addEventListener("touchstart", this._onPointerDown, {
			passive: false,
			capture: true,
		});
		this.addEventListener("mousedown", this._onPointerDown, {
			passive: false,
			capture: true,
		});

		this.addEventListener(
			"flickity:ready",
			this._preloadNextImages.bind(this)
		);
		this.addEventListener(
			"flickity:slide-changed",
			this._preloadNextImages.bind(this)
		);

		this.addEventListener(
			"click",
			(event) => {
				const LoadingStore = window.LoadingStore;
				if (LoadingStore && LoadingStore.value) {
					const navLink = event.target?.closest?.("a[href]");
					if (!navLink) {
						event.preventDefault();
						event.stopPropagation();
						return;
					}
				}
			},
			true
		);
	}

	_onPointerDown(e) {
		const now = performance.now();
		const hardGate = now < this._allowAfter;

		if (!this._isReady || hardGate) {
			e.preventDefault();
			e.stopPropagation();
			if (!this._isInitializing && !hardGate) {
				this._safeInit();
			}
			return false;
		}
	}

	async _safeInit() {
		if (this._isInitializing || this._isReady) return;
		this._isInitializing = true;
		this._disableInteractions();

		try {
			if (
				window.LoadingStore &&
				typeof window.LoadingStore.whenFalseOnce === "function"
			) {
				await window.LoadingStore.whenFalseOnce();
			}
		} catch (_) {}

		try {
			await this._createFlickity();
		} catch (err) {
			console.warn("[CPCarousel] init error:", err);
		} finally {
			this._isInitializing = false;
		}
	}

	connectedCallback() {
		if (this._connected) return;
		this._connected = true;

		if (
			window.CarouselPool &&
			typeof window.CarouselPool.observe === "function"
		) {
			window.CarouselPool.observe(this);
		}

		if (
			window.LoadingStore &&
			typeof window.LoadingStore.subscribe === "function"
		) {
			this._unsubLoading = window.LoadingStore.subscribe((isLoading) => {
				if (isLoading) {
					this._isDisabledByLoading = true;
					this._disableInteractions();
				} else {
					this._isDisabledByLoading = false;
					const r = this.getBoundingClientRect();
					const onScreen =
						r.top < window.innerHeight + 300 && r.bottom > -300;
					if (!this._isReady && !this._isInitializing && onScreen) {
						this._safeInit();
					} else if (this._isReady) {
						this._enableInteractions();
					}
				}
			});
		}

		this._onAjaxLoading = () => this._disableInteractions();
		this._onAjaxLoaded = () => this._scheduleEnable(120);
		this._onGatesClear = () => this._enableInteractions();
		document.addEventListener("ed:ajaxinate:loading", this._onAjaxLoading);
		document.addEventListener("ed:ajaxinate:loaded", this._onAjaxLoaded);
		document.addEventListener(
			"ed:carousel-gates:clear",
			this._onGatesClear
		);

		const r = this.getBoundingClientRect();
		if (
			(!window.LoadingStore || !window.LoadingStore.value) &&
			r.top < window.innerHeight + 300 &&
			r.bottom > -300
		) {
			this._safeInit();
		}
	}

	_updateDraggableState(flickityInstance) {
		if (!flickityInstance || !flickityInstance.options) return;

		if (this.childElementCount >= 2) {
			flickityInstance.options.draggable = ">1";
		} else {
			flickityInstance.options.draggable = false;
		}
		if (typeof flickityInstance.updateDraggable === "function") {
			flickityInstance.updateDraggable();
		}
	}

	async disconnectedCallback() {
		this._connected = false;

		if (
			window.CarouselPool &&
			typeof window.CarouselPool.unobserve === "function"
		) {
			window.CarouselPool.unobserve(this);
		}

		try {
			this.removeEventListener("touchstart", this._onPointerDown, true);
			this.removeEventListener("mousedown", this._onPointerDown, true);
		} catch (_) {}
		if (this._unsubLoading) {
			try {
				this._unsubLoading();
			} catch (e) {
				// Silent fail
			}
		}

		document.removeEventListener(
			"ed:ajaxinate:loading",
			this._onAjaxLoading
		);
		document.removeEventListener("ed:ajaxinate:loaded", this._onAjaxLoaded);
		document.removeEventListener(
			"ed:carousel-gates:clear",
			this._onGatesClear
		);
		this._onAjaxLoading = this._onAjaxLoaded = this._onGatesClear = null;

		if (this._pendingEnableTimer) {
			clearTimeout(this._pendingEnableTimer);
			this._pendingEnableTimer = null;
		}

		if (this.flickity) {
			try {
				const flickityInstance = await this.flickity;
				if (
					flickityInstance &&
					typeof flickityInstance.destroy === "function"
				) {
					flickityInstance.destroy();
				}
			} catch (e) {
				// Silent fail
			}
		}
	}

	_scheduleEnable(delay = 80) {
		clearTimeout(this._pendingEnableTimer);
		this._pendingEnableTimer = setTimeout(
			() => this._enableInteractions(),
			delay
		);
	}

	_disableInteractions() {
		this.setAttribute("aria-busy", "true");
		this.style.pointerEvents = "none";
		if (this._root) this._root.style.pointerEvents = "none";

		if (this.flickity) {
			Promise.resolve(this.flickity)
				.then((inst) => {
					if (!inst) return;
					inst.options.draggable = false;
					if (inst.updateDraggable) inst.updateDraggable();
					if (inst.isDragging && inst.pointerUp) inst.pointerUp();
					if (inst.viewport) {
						inst.viewport.style.pointerEvents = "none";
						inst.viewport.style.touchAction = "none";
					}
				})
				.catch(() => {});
		}
	}

	async _enableInteractions() {
		if (!this._isReady) return false;

		const gatesUp =
			document.body.hasAttribute("data-ajaxinate-loading") ||
			document.body.hasAttribute("data-carousel-embargo");
		if (gatesUp) {
			this._scheduleEnable(200);
			return false;
		}

		this.removeAttribute("aria-busy");
		this.style.pointerEvents = "";
		if (this._root) this._root.style.pointerEvents = "";

		const inst = await this.flickity;
		if (inst && inst.viewport) {
			inst.viewport.style.pointerEvents = "";
			inst.viewport.style.touchAction = "";
			inst.options.draggable = this.childElementCount >= 2 ? ">1" : false;
			if (typeof inst.updateDraggable === "function")
				inst.updateDraggable();
			if (typeof inst.resize === "function") inst.resize();
		}

		return true;
	}

	disableInteractions() {
		this._disableInteractions();
	}

	enableInteractions() {
		this._enableInteractions();
	}

	get flickityConfig() {
		return JSON.parse(this.getAttribute("flickity-config"));
	}

	get flickityInstance() {
		return this.flickity;
	}

	async next() {
		if (!this.flickity) return;
		try {
			const inst = await this.flickityInstance;
			if (inst && inst.isActive) inst.next();
		} catch (e) {
			// Silent fail
		}
	}

	async previous() {
		if (!this.flickity) return;
		try {
			const inst = await this.flickityInstance;
			if (inst && inst.isActive) inst.previous();
		} catch (e) {
			// Silent fail
		}
	}

	async select(indexOrSelector) {
		if (!this.flickity) return;
		try {
			const inst = await this.flickityInstance;
			if (inst && inst.isActive) inst.selectCell(indexOrSelector);
		} catch (e) {
			// Silent fail
		}
	}

	async setDraggable(draggable) {
		try {
			const flickityInstance = await this.flickity;
			if (flickityInstance && flickityInstance.options) {
				flickityInstance.options.draggable = draggable;
				if (typeof flickityInstance.updateDraggable === "function") {
					flickityInstance.updateDraggable();
				}
			}
		} catch (e) {
			// Silent fail
		}
	}

	async reload() {
		try {
			const flickityInstance = await this.flickity;
			if (
				flickityInstance &&
				typeof flickityInstance.destroy === "function"
			) {
				flickityInstance.destroy();
			}
		} catch (e) {
			// Silent fail
		}
		this._createFlickity(true);
	}

	_destroyFlickity() {
		if (this.flickity) {
			Promise.resolve(this.flickity)
				.then((inst) => {
					if (inst && typeof inst.destroy === "function") {
						try {
							inst.destroy();
						} catch (e) {
							// Silent fail
						}
					}
				})
				.catch(() => {});
		}
		this.flickity = null;
		this._isReady = false;
		this._isInitializing = false;
		this.classList.remove("is-ready");
		this.removeAttribute("data-carousel-booted");

		if (this._pendingEnableTimer) {
			clearTimeout(this._pendingEnableTimer);
			this._pendingEnableTimer = null;
		}
	}

	async _createFlickity(isReload = false) {
		if (this.flickity) {
			return this.flickity;
		}
		this.flickity = new Promise(async (resolve) => {
			const responsiveConf = this.flickityConfig || {};

			let additionalConf = {
				wrapAround: true,
				prevNextButtons: true,
				draggable: false,
			};

			if (this.childElementCount < 2) {
				additionalConf = {
					wrapAround: false,
					prevNextButtons: false,
					draggable: false,
				};
			}

			if (
				window.LibraryLoader &&
				typeof window.LibraryLoader.load === "function"
			) {
				await window.LibraryLoader.load("flickity");
			} else if (!window.ThemeFlickity) {
				await new Promise((res) => {
					let tries = 0;
					const iv = setInterval(() => {
						tries++;
						if (window.ThemeFlickity || tries > 40) {
							clearInterval(iv);
							res();
						}
					}, 25);
				});
			}

			if (!isReload) {
				await this._untilVisible({
					rootMargin: "150px",
					threshold: 0,
				});
			}

			const FlickityCtor =
				typeof window.ThemeFlickity === "function"
					? window.ThemeFlickity
					: typeof window.Flickity === "function"
						? window.Flickity
						: null;

			if (!FlickityCtor) {
				console.warn("[CPCarousel] Flickity not found yet");
				const self = this;
				setTimeout(() => {
					if (!self._isReady && !self._isInitializing) {
						self._createFlickity(true);
					}
				}, 200);
				return resolve(null);
			}

			const carouselElement = this;

			const flickityInstance = new FlickityCtor(this, {
				...{
					rightToLeft: document && document.dir === "rtl",
					accessibility:
						window.matchMedia &&
						window.matchMedia("(pointer: fine)").matches,
					lazyLoad: false,
					imagesLoaded: true,
					on: {
						ready: (event) => {
							const inst = this;
							carouselElement._triggerEvent(
								"flickity:ready",
								event
							);
							carouselElement._preloadNextImages();

							const paginationStyle =
								carouselElement.getAttribute(
									"data-pagination-style"
								) || "dots";
							const stripesVariant =
								carouselElement.getAttribute(
									"data-stripes-variant"
								) || "light";
							const dotsContainer = carouselElement.querySelector(
								".flickity-page-dots"
							);
							if (dotsContainer) {
								dotsContainer.classList.remove(
									"flickity-page-dots--dots",
									"flickity-page-dots--stripes",
									"flickity-page-dots--hidden",
									"flickity-page-dots--stripes-dark",
									"flickity-page-dots--stripes-light",
									"stripes-visible",
									"pagination-ready"
								);
								if (paginationStyle !== "dots") {
									dotsContainer.classList.add(
										`flickity-page-dots--${paginationStyle}`
									);
									if (paginationStyle === "stripes") {
										dotsContainer.classList.add(
											`flickity-page-dots--stripes-${stripesVariant}`
										);
										setTimeout(() => {
											dotsContainer.classList.add(
												"stripes-visible"
											);
										}, 50);
									} else {
										dotsContainer.classList.add(
											"pagination-ready"
										);
									}
								} else {
									dotsContainer.classList.add(
										"pagination-ready"
									);
								}
							}

							carouselElement._isReady = true;
							carouselElement.classList.add("is-ready");

							carouselElement.setAttribute(
								"data-carousel-embargo",
								"1"
							);
							carouselElement.setAttribute("aria-busy", "true");
							carouselElement.style.pointerEvents = "none";
							if (carouselElement._root) {
								carouselElement._root.style.pointerEvents =
									"none";
							}

							const READY_EMBARGO_MS = 500;

							setTimeout(() => {
								if (!carouselElement.isConnected) return;

								carouselElement.removeAttribute(
									"data-carousel-embargo"
								);
								carouselElement.removeAttribute("aria-busy");
								carouselElement.style.pointerEvents = "";
								if (carouselElement._root) {
									carouselElement._root.style.pointerEvents =
										"";
								}

								try {
									if (inst) {
										if (
											carouselElement.childElementCount >=
											2
										) {
											inst.options.draggable = ">1";
										} else {
											inst.options.draggable = false;
										}
										if (
											typeof inst.updateDraggable ===
											"function"
										) {
											inst.updateDraggable();
										}
										if (typeof inst.resize === "function") {
											inst.resize();
										}
									}
								} catch (_) {}

								if (
									!document.body.hasAttribute(
										"data-carousel-embargo"
									) &&
									!document.body.hasAttribute(
										"data-ajaxinate-loading"
									)
								) {
									if (carouselElement._enableInteractions) {
										carouselElement._enableInteractions();
									}
								}
							}, READY_EMBARGO_MS);

							const LoadingStore = window.LoadingStore;
							if (
								LoadingStore &&
								typeof LoadingStore.whenFalseOnce === "function"
							) {
								LoadingStore.whenFalseOnce()
									.then(() => {
										//
									})
									.catch(() => {
										//
									});
							}
						},
						change: (event) =>
							carouselElement._triggerEvent(
								"flickity:slide-changed",
								event
							),
						settle: (event) =>
							carouselElement._triggerEvent(
								"flickity:slide-settled",
								event
							),
					},
				},
				...additionalConf,
				...responsiveConf,
			});

			if (flickityInstance && flickityInstance.on) {
				flickityInstance.on("dragStart", () => {
					carouselElement._isDragging = true;

					const LoadingStore = window.LoadingStore;
					const pageLoading = document.body.hasAttribute(
						"data-ajaxinate-loading"
					);
					if (pageLoading || (LoadingStore && LoadingStore.value)) {
						try {
							flickityInstance.dragEnd();
						} catch (_) {}
					}
				});

				flickityInstance.on("dragEnd", () => {
					carouselElement._isDragging = false;
				});

				const updateImageLoader = (cellElem) => {
					if (!cellElem) return;
					const loader = cellElem.querySelector(
						".mobile-carousel-image-loader"
					);
					const img = cellElem.querySelector(
						"img.product-item__mobile-image"
					);
					if (!loader || !img) return;

					const isLoaded = img.complete && img.naturalWidth > 0;

					if (isLoaded) {
						loader.style.opacity = "0";
						loader.style.display = "none";
					} else {
						loader.style.display = "flex";
						loader.style.opacity = "1";

						const hideLoader = () => {
							requestAnimationFrame(() => {
								requestAnimationFrame(() => {
									loader.style.opacity = "0";
									setTimeout(() => {
										loader.style.display = "none";
									}, 400);
								});
							});
						};

						img.addEventListener("load", hideLoader, {
							once: true,
						});
						img.addEventListener("error", hideLoader, {
							once: true,
						});
					}
				};

				flickityInstance.on("select", () => {
					try {
						updateImageLoader(flickityInstance.selectedElement);
					} catch (error) {
						// Silent fail
					}
				});
			}

			resolve(flickityInstance);
		});
	}

	async _preloadNextImages() {
		if (!this.flickity) return;

		try {
			const flickityInstance = await this.flickityInstance;
			if (!flickityInstance || !flickityInstance.isActive) return;

			const currentIndex = flickityInstance.selectedIndex;
			const cells = flickityInstance.getCellElements();
			const images = [];

			const preloadCount = Math.min(3, cells.length - currentIndex - 1);

			for (
				let i = currentIndex + 1;
				i <= currentIndex + preloadCount;
				i++
			) {
				if (i >= cells.length) break;
				const cell = cells[i];
				const img = cell.querySelector(
					"img.product-item__mobile-image"
				);
				if (img) {
					images.push(img);
				}
			}

			images.forEach((img) => {
				if (img.dataset.srcset && !img.srcset) {
					img.srcset = img.dataset.srcset;
					img.removeAttribute("data-srcset");
				}
				if (img.getAttribute("loading") === "lazy") {
					img.removeAttribute("loading");
				}
				if (window.observer && !window.observedImages?.has(img)) {
					try {
						window.observer.observe(img);
						if (window.observedImages) {
							window.observedImages.add(img);
						}
					} catch (e) {
						// Silent fail
					}
				}
			});
		} catch (e) {
			// Silent fail
		}
	}

	_triggerEvent(name, data = {}) {
		this.dispatchEvent(
			new CustomEvent(name, {
				bubbles: true,
				detail: data,
			})
		);
	}

	_untilVisible(
		intersectionObserverOptions = { rootMargin: "30px 0px", threshold: 0 }
	) {
		return new Promise((resolve) => {
			if (window.IntersectionObserver) {
				const observer = new IntersectionObserver((entries) => {
					if (entries[0].isIntersecting) {
						observer.disconnect();
						resolve();
					}
				}, intersectionObserverOptions);
				observer.observe(this);
			} else {
				resolve();
			}
		});
	}
};

window.customElements.define(
	"flickity-carousel-mobile-collection-cards",
	FlickityCarouselMobileCollectionCards
);

const CarouselPool = (() => {
	const MAX = 6;
	const DESTROY_DISTANCE = 2200;
	const items = new Set();
	let io;

	function ensureIO() {
		if (io) return io;
		io = new IntersectionObserver(
			(entries) => {
				entries.forEach((e) => {
					const el = e.target;
					if (
						el.tagName !==
						"FLICKITY-CAROUSEL-MOBILE-COLLECTION-CARDS"
					) {
						return;
					}
					if (e.isIntersecting) {
						items.add(el);
						trim();

						if (
							el._safeInit &&
							!el._isReady &&
							!el._isInitializing &&
							!el.flickity
						) {
							el._safeInit();
						}
					} else {
					}
				});
			},
			{ rootMargin: "600px 0px" }
		);
		return io;
	}

	function trim() {
		return;
	}

	function observe(el) {
		ensureIO().observe(el);
	}
	function unobserve(el) {
		if (!io) return;
		try {
			io.unobserve(el);
		} catch (_) {}
		items.delete(el);
	}

	function getActive() {
		return Array.from(items).filter((el) => el.isConnected && el.flickity);
	}

	return { observe, unobserve, getActive };
})();

window.CarouselPool = CarouselPool;

window.addEventListener("pageshow", (e) => {
	if (e.persisted) {
		document
			.querySelectorAll("flickity-carousel-mobile-collection-cards")
			.forEach((carousel) => {
				carousel._isReady = false;
				carousel._isInitializing = false;
				carousel.setAttribute("aria-busy", "true");
				carousel.style.pointerEvents = "none";
				carousel.classList.remove("is-ready");

				carousel._destroyFlickity();
				carousel._safeInit();
			});
	}
});
